// 1. Imports
import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  CircularProgress,
  Chip,
  Box,
  Checkbox,
  ListItemText,
} from "@mui/material";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { toast } from "react-toastify";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  selectedAdmin = null,
  onDataChange,
}) => {
  // State for form data
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    credential: "",
    password: "",
    confirmPassword: "",
    areas: [],
    governorates: [],
  });


  // State for credentials list
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(false);

  // State for areas and governorates data
  const [areasData, setAreasData] = useState([]);
  const [governoratesData, setGovernoratesData] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);

  // Process and organize data from API
  const processGovernoratesData = (data) => {
    const areasMap = new Map();
    const governorates = [];

    data.forEach((item) => {
      // Add area to map if not exists
      if (item.area && !areasMap.has(item.area._id)) {
        areasMap.set(item.area._id, {
          _id: item.area._id,
          name: item.area.name,
          areaId: item.area.areaId,
        });
      }

      // Add governorate
      governorates.push({
        _id: item._id,
        governorateId: item.governorateId,
        name: item.name,
        areaId: item.area._id,
        areaName: item.area.name,
      });
    });

    return {
      areas: Array.from(areasMap.values()),
      governorates,
    };
  };

  // Get available governorates based on selected areas
  const getAvailableGovernorates = () => {
    if (!formData.areas || formData.areas.length === 0) {
      return [];
    }

    return governoratesData.filter((gov) =>
      formData.areas.includes(gov.areaId)
    );
  };

  // Effect to populate form data when editing
// Effect to populate form data when editing
useEffect(() => {
  if (selectedAdmin && currentView === "editAdmin") {
    setFormData({
      name: selectedAdmin.name || "",
      phoneNumber: selectedAdmin.phoneNumber || "",
      email: selectedAdmin.email || "",
      credential:
        selectedAdmin.credential?._id || selectedAdmin.credential || "",
      password: "",
      confirmPassword: "",
      // Extract _id from areas array (handle both object and string formats)
      areas: Array.isArray(selectedAdmin.areas)
        ? selectedAdmin.areas.map((area) =>
            typeof area === "object" ? area._id : area
          )
        : [],
      // Extract _id from governorates array (handle both object and string formats)
      governorates: Array.isArray(selectedAdmin.governorates)
        ? selectedAdmin.governorates.map((gov) =>
            typeof gov === "object" ? gov._id : gov
          )
        : [],
    });
  } else if (currentView === "createAdmin") {
    // Reset form for create mode
    setFormData({
      name: "",
      phoneNumber: "",
      email: "",
      credential: "",
      password: "",
      confirmPassword: "",
      areas: [],
      governorates: [],
    });
  }
}, [selectedAdmin, currentView, open]);

  // Fetch credentials when component mounts or when drawer opens
  useEffect(() => {
    if (
      open &&
      (currentView === "createAdmin" || currentView === "editAdmin")
    ) {
      fetchCredentials();
      fetchAreasAndGovernorates();
    }
  }, [open, currentView]);

  // Function to fetch credentials
  const fetchCredentials = async () => {
    try {
      setCredentialsLoading(true);
      const response = await api.get("/credentials");
      setCredentials(response.data.data || []);
    } catch (error) {
      console.error("Error fetching credentials:", error);
      toast.error("حدث خطأ أثناء تحميل الصلاحيات");
    } finally {
      setCredentialsLoading(false);
    }
  };

  // Function to fetch areas and governorates
  const fetchAreasAndGovernorates = async () => {
    try {
      setAreasLoading(true);
      const response = await api.get("/admin/governorates");
      
      if (response.data.status === "success" && response.data.data) {
        const { areas, governorates } = processGovernoratesData(
          response.data.data
        );
        setAreasData(areas);
        setGovernoratesData(governorates);
      }
    } catch (error) {
      console.error("Error fetching areas and governorates:", error);
      toast.error("حدث خطأ أثناء تحميل المناطق والمحافظات");
    } finally {
      setAreasLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle areas change
  const handleAreasChange = (event) => {
    const {
      target: { value },
    } = event;

    const newAreas = typeof value === "string" ? value.split(",") : value;

    setFormData((prev) => {
      // Filter out governorates that belong to removed areas
      const removedAreas = prev.areas.filter(
        (areaId) => !newAreas.includes(areaId)
      );

      let updatedGovernorates = [...prev.governorates];

      if (removedAreas.length > 0) {
        // Remove governorates that belong to removed areas
        updatedGovernorates = updatedGovernorates.filter((govId) => {
          const gov = governoratesData.find((g) => g._id === govId);
          return gov && !removedAreas.includes(gov.areaId);
        });
      }

      return {
        ...prev,
        areas: newAreas,
        governorates: updatedGovernorates,
      };
    });
  };

  // Handle governorates change
  const handleGovernoratesChange = (event) => {
    const {
      target: { value },
    } = event;

    const newGovernorates =
      typeof value === "string" ? value.split(",") : value;

    setFormData((prev) => ({
      ...prev,
      governorates: newGovernorates,
    }));
  };

  // Get area name by ID
  const getAreaName = (areaId) => {
    const area = areasData.find((a) => a._id === areaId);
    return area ? area.name.ar : areaId;
  };

  // Get governorate name by ID
  const getGovernorateName = (govId) => {
    const gov = governoratesData.find((g) => g._id === govId);
    return gov ? gov.name.ar : govId;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.phoneNumber ||
      !formData.credential
    ) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    // Areas validation
    if (!formData.areas || formData.areas.length === 0) {
      toast.error("يرجى اختيار منطقة واحدة على الأقل");
      return;
    }

    // Governorates validation
    if (!formData.governorates || formData.governorates.length === 0) {
      toast.error("يرجى اختيار محافظة واحدة على الأقل");
      return;
    }

    // Password validation for create mode
    if (currentView === "createAdmin") {
      if (!formData.password || !formData.confirmPassword) {
        toast.error("يرجى إدخال كلمة المرور وتأكيدها");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error("كلمة المرور وتأكيدها غير متطابقان");
        return;
      }
    }

    // Password validation for edit mode (only if password is provided)
    if (currentView === "editAdmin" && formData.password) {
      if (formData.password !== formData.confirmPassword) {
        toast.error("كلمة المرور وتأكيدها غير متطابقان");
        return;
      }
    }

    try {
      setLoading(true);

      let response;
      let successMessage;

      // Prepare data payload
      const dataPayload = {
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        credential: formData.credential,
        areas: formData.areas, // Send area _ids
        governorates: formData.governorates, // Send governorate _ids
      };

      if (currentView === "createAdmin") {
        // Create new sub-admin
        dataPayload.password = formData.password;
        dataPayload.confirmPassword = formData.confirmPassword;

        response = await api.post("/subAdmins", dataPayload);
        successMessage = "تم إضافة المسؤول بنجاح";
      } else if (currentView === "editAdmin" && selectedAdmin) {
        // Update existing sub-admin
        // Only include password if it's provided
        if (formData.password) {
          dataPayload.password = formData.password;
        }

        response = await api.patch(
          `/subAdmins/${selectedAdmin._id}/updateData`,
          dataPayload
        );
        successMessage = "تم تحديث المسؤول بنجاح";
      }

      toast.success(successMessage);

      // Reset form
      setFormData({
        name: "",
        phoneNumber: "",
        email: "",
        credential: "",
        password: "",
        confirmPassword: "",
        areas: [],
        governorates: [],
      });

      // Close modal and refresh data
      onClose();
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage =
        error.response?.data?.message ||
        (currentView === "createAdmin"
          ? "حدث خطأ أثناء إضافة المسؤول"
          : "حدث خطأ أثناء تحديث المسؤول");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderCreateAdminView = () => (
    <div className="user-modal">
      <p className="user-modal-p">
        {currentView === "createAdmin"
          ? "اضافة مسؤول فرعي"
          : "تعديل مسؤول فرعي"}
      </p>
      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="name-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الاسم
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="name-input"
            name="name"
            type="text"
            notched
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="phone-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            رقم الموبايل
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="phone-input"
            name="phoneNumber"
            type="text"
            notched
            value={formData.phoneNumber}
            onChange={handleInputChange}
            required
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="email-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            البريد الالكتروني
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="email-input"
            name="email"
            type="email"
            notched
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </FormControl>

        <FormControl fullWidth required>
          <InputLabel
            htmlFor="credential-select"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الصلاحية
          </InputLabel>
          <Select
            labelId="credential-select-label"
            id="credential-select"
            displayEmpty
            sx={{ borderRadius: "8px" }}
            name="credential"
            value={formData.credential}
            onChange={handleInputChange}
            required
          >
            <MenuItem value="">
              <em>اختر الصلاحية</em>
            </MenuItem>
            {credentialsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} /> جاري التحميل...
              </MenuItem>
            ) : (
              credentials.map((credential) => (
                <MenuItem key={credential._id} value={credential._id}>
                  {credential.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Areas Multi-Select */}
        <FormControl fullWidth required>
          <InputLabel
            htmlFor="areas-select"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            المناطق
          </InputLabel>
          <Select
            labelId="areas-select-label"
            id="areas-select"
            multiple
            displayEmpty
            sx={{ borderRadius: "8px" }}
            value={formData.areas}
            onChange={handleAreasChange}
            disabled={areasLoading}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>اختر المناطق</em>;
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={getAreaName(value)}
                      size="small"
                    />
                  ))}
                </Box>
              );
            }}
            required
          >
            <MenuItem disabled value="">
              <em>{areasLoading ? "جاري التحميل..." : "اختر المناطق"}</em>
            </MenuItem>
            {areasLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} /> جاري التحميل...
              </MenuItem>
            ) : (
              areasData.map((area) => (
                <MenuItem key={area._id} value={area._id}>
                  <Checkbox checked={formData.areas.indexOf(area._id) > -1} />
                  <ListItemText primary={area.name.ar} />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Governorates Multi-Select */}
        <FormControl
          fullWidth
          required
          disabled={formData.areas.length === 0 || areasLoading}
        >
          <InputLabel
            htmlFor="governorates-select"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            المحافظات
          </InputLabel>
          <Select
            labelId="governorates-select-label"
            id="governorates-select"
            multiple
            displayEmpty
            sx={{ borderRadius: "8px" }}
            value={formData.governorates}
            onChange={handleGovernoratesChange}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>اختر المحافظات</em>;
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={getGovernorateName(value)}
                      size="small"
                    />
                  ))}
                </Box>
              );
            }}
            required
          >
            <MenuItem disabled value="">
              <em>
                {formData.areas.length === 0
                  ? "يرجى اختيار المناطق أولاً"
                  : "اختر المحافظات"}
              </em>
            </MenuItem>
            {getAvailableGovernorates().map((gov) => (
              <MenuItem key={gov._id} value={gov._id}>
                <Checkbox
                  checked={formData.governorates.indexOf(gov._id) > -1}
                />
                <ListItemText
                  primary={gov.name.ar}
                  secondary={`(${gov.areaName.ar})`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          sx={{ width: "100%" }}
          variant="outlined"
          required={currentView === "createAdmin"}
        >
          <InputLabel
            htmlFor="password-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            {currentView === "editAdmin"
              ? "كلمة المرور الجديدة (اختيارية)"
              : "كلمة المرور"}
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="password-input"
            name="password"
            type="password"
            notched
            value={formData.password}
            onChange={handleInputChange}
            required={currentView === "createAdmin"}
          />
        </FormControl>

        <FormControl
          sx={{ width: "100%" }}
          variant="outlined"
          required={currentView === "createAdmin" || formData.password}
        >
          <InputLabel
            htmlFor="confirm-password-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            تأكيد كلمة المرور
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="confirm-password-input"
            name="confirmPassword"
            type="password"
            notched
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required={currentView === "createAdmin" || formData.password}
          />
        </FormControl>

        <BlueButton
          text={
            loading
              ? currentView === "createAdmin"
                ? "جاري الإضافة..."
                : "جاري التحديث..."
              : currentView === "createAdmin"
              ? "اضافة"
              : "تحديث"
          }
          size="medium"
          variant="fill"
          type="submit"
          disabled={loading || areasLoading}
        />
      </form>
    </div>
  );

  const renderDefaultView = () => <div></div>;

  const views = {
    createAdmin: renderCreateAdminView,
    editAdmin: renderCreateAdminView,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;